import React from "react";

import { Gem, X } from "lucide-react";
import UpsellButton from "./UpsellButton";
import { userEventTracker } from "../shared/banners/DashboardBanners";
import { Button, ButtonVariant } from "@merge-api/merge-javascript-shared";
import { useFlow } from "@frigade/react";
import { upgradeRequestFlowId } from "../../router/FrigadeProvider";
import { GET_IN_TOUCH_PATH } from "../../router/RouterUtils";

type Props = {
  modalTitle: string;
  customImage: JSX.Element;
  modalBodyText: JSX.Element;
  includeLearnMoreButton: boolean;
  learnMoreRedirect?: string;
  includeModalCloseOption: boolean;
  showModal: boolean;
  setShowModal: (isOpen: boolean) => void;
  modalWidthValue?: string;
  upsellButtonId: string;
  learnMoreButtonId: string;
};

const UpsellModal = ({
  modalTitle,
  customImage,
  modalBodyText,
  includeLearnMoreButton,
  learnMoreRedirect,
  includeModalCloseOption,
  showModal,
  setShowModal,
  modalWidthValue,
  upsellButtonId,
  learnMoreButtonId,
}: Props) => {
  const { flow } = useFlow(upgradeRequestFlowId);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    // The overlay spans the viewport so the modal centers against it, but stays
    // click-through so the page behind remains usable when there is no close option.
    // z-index must clear the legacy Dashkit nav (.navbar[class*="fixed-"] is 1030), and the
    // left padding keeps centering inside the content area rather than under the 250px
    // fixed sidebar, which only exists from the md breakpoint up.
    <div className="fixed inset-0 z-[1040] flex items-center justify-center pointer-events-none md:pl-[250px]">
      {showModal && (
        // overflow-x is clipped explicitly: a visible x-axis computes to auto when the
        // y-axis is not visible, which would let intentionally bleeding artwork scroll
        <div
          className="pt-5 pr-6 pl-6 pb-6 bg-white rounded-lg shadow-md relative max-h-[90vh] overflow-x-hidden overflow-y-auto pointer-events-auto"
          style={{
            width: modalWidthValue,
          }}
        >
          {includeModalCloseOption && (
            <Button
              onClick={handleCloseModal}
              size="sm"
              variant={ButtonVariant.IconShadowHidden}
              className="absolute top-2.5 right-2.5"
            >
              <X size={12} />
            </Button>
          )}
          <div className="flex flex-wrap mb-3 ml-0 mr-0">
            <div className="py-0.5 px-2 rounded-3xl font-semibold title-sm flex items-center bg-premium-gold-gradient">
              <Gem size={10} className="mr-1 mb-0" />
              Premium feature
            </div>
          </div>

          <h3 className="mb-6">{modalTitle}</h3>
          {customImage}
          {modalBodyText}
          <div className="flex flex-row gap-3">
            {includeLearnMoreButton && (
              <UpsellButton
                className="flex"
                onClick={() => {
                  window.open(learnMoreRedirect, "_blank");
                  userEventTracker({
                    event_name_input: learnMoreButtonId,
                    impressions_input: 0,
                    dismisses_input: 0,
                    clicks_input: 1,
                  });
                }}
                buttonContent="Learn more"
                buttonColor="solidGrey"
                hasAdvancedHoverState
                id={learnMoreButtonId}
              />
            )}
            <UpsellButton
              className="flex "
              onClick={() => {
                flow?.skip();
                window.open(GET_IN_TOUCH_PATH, "_blank");
                userEventTracker({
                  event_name_input: upsellButtonId,
                  impressions_input: 0,
                  dismisses_input: 0,
                  clicks_input: 1,
                });
              }}
              buttonContent="Request upgrade"
              buttonColor="gradientGold"
              hasAdvancedHoverState
              id={upsellButtonId}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UpsellModal;
